import { useNuxtApp, useRuntimeConfig } from "#imports";
import { isObject } from "@spoferan/spoferan-ts-core";
import { EXISTING_FORM_INPUT_VALUE } from "../config/index.mjs";
import { isRef } from "vue";
export function useFetchUtils() {
  const { $sentry } = useNuxtApp();
  const addApiRequestBreadcrumb = (request, options, response) => {
    if ($sentry) {
      const breadcrumb = {
        type: "http",
        level: "info",
        category: "xhr",
        data: {
          url: typeof request !== "string" ? request.url : request,
          method: options.method ?? "GET",
          status_code: response.status,
          reason: response.statusText
        }
      };
      $sentry.addBreadcrumb(breadcrumb);
    }
  };
  function buildApiRequestHeaders(options) {
    const { $auth, $i18n, $echo } = useNuxtApp();
    const nuxtConfig = useRuntimeConfig();
    const headers = options?.headers ?? {};
    if (!headers["Accept"]) {
      headers["Accept"] = "application/json, text/plain, */*";
    }
    if (!headers["Authorization"]) {
      const authToken = $auth?.bearerToken ?? null;
      headers["Authorization"] = options?.guest || !authToken ? nuxtConfig.public.apiAuthToken : authToken;
    }
    if (process.server) {
      headers["Origin"] = nuxtConfig.public.appUrl;
    }
    if ($i18n) {
      const currentLocale = $i18n.locales.value.find((locale) => locale.code === $i18n.locale.value);
      if (currentLocale && currentLocale.iso) {
        headers["Accept-Language"] = currentLocale.iso;
      }
    }
    const method = options?.method ?? "GET";
    if ($echo && !["get", "options"].includes(method.toLowerCase())) {
      const connectionId = $echo.getConnectionId();
      if (connectionId) {
        headers["X-Socket-ID"] = connectionId;
      }
    }
    return headers;
  }
  function buildApiErrorPayload(response, error) {
    const errorPayload = {
      error,
      statusCode: response?.status ?? 500,
      statusMessage: response?.statusText,
      message: response._data?.display_message ?? response._data?.message ?? response._data?.alert?.message,
      // Only for validation errors
      errors: response._data?.errors,
      notification: response._data?.notification,
      alert: response._data?.alert,
      // Only defined on dev
      stack: response._data?.exception ? JSON.stringify({
        original_message: response._data?.original_message,
        exception: response._data?.exception,
        line: response._data?.line
      }) : void 0
    };
    Object.keys(errorPayload).forEach((key) => errorPayload[key] === void 0 && delete errorPayload[key]);
    return errorPayload;
  }
  function logApiError(errorPayload) {
    const isClientError = errorPayload.statusCode >= 400 && errorPayload.statusCode < 500;
    if (!isClientError) {
      const logMessage = `Error [${errorPayload.statusCode}] while sending API request: ${errorPayload.message}`;
      console.error(logMessage, errorPayload);
      if ($sentry) {
        if (errorPayload.error) {
          $sentry.captureException(errorPayload.error);
        } else {
          $sentry.captureMessage(logMessage);
        }
      }
    }
  }
  function parseFormData(data, propNamePrefix = null) {
    let parsedData = {};
    if (data && Array.isArray(data) && data.length) {
      data.forEach((itValue, itKey) => {
        const propName = propNamePrefix ? `${propNamePrefix}[${itKey}]` : itKey.toString();
        parsedData = { ...parsedData, ...parseFormValueAsFlatObject(propName, itValue) };
      });
    } else if (data && isObject(data) && Object.keys(data).length) {
      Object.keys(data).forEach((itKey) => {
        const propName = propNamePrefix ? `${propNamePrefix}[${itKey}]` : itKey;
        const itValue = data[itKey];
        parsedData = { ...parsedData, ...parseFormValueAsFlatObject(propName, itValue) };
      });
    } else if (propNamePrefix) {
      parsedData[propNamePrefix] = [];
    }
    return parsedData;
  }
  function parseFormValueAsFlatObject(key, value) {
    const preventSendValues = [void 0, EXISTING_FORM_INPUT_VALUE];
    let data = {};
    if (isRef(value)) {
      data[key] = value;
    } else if (typeof FileList !== "undefined" && value instanceof FileList) {
      value = Array.from(value);
      for (let i = 0; i < value.length; i++) {
        data[`${key}[${i}]`] = value[i] !== null && value[i] !== void 0 ? value[i] : "";
      }
    } else if (Array.isArray(value) || isObject(value) && !(value instanceof File)) {
      data = parseFormData(value, key);
    } else if (!preventSendValues.includes(value)) {
      data[key] = value !== null ? value : "";
    }
    return data;
  }
  return {
    buildApiRequestHeaders,
    parseFormData,
    addApiRequestBreadcrumb,
    buildApiErrorPayload,
    logApiError
  };
}
